/**
 * DecisionFormDialog — J2-C (broadened J5)
 *
 * Wave 4c: converted from Modal (2 tabs) → FormSheet (4 tabs).
 *
 * CREATE path (no decision prop): 4 required fields.
 * EDIT path (decision prop passed): also shows advisory consultation,
 *   trial-period toggle, and evidence links.
 *
 * 4 tabs:
 *   1. Details    — core fields (title, decision, rationale, domain, impact, method)
 *   2. Method Library — full method picker
 *   3. Advisory   — advisory board consultation
 *   4. Evidence   — trial period toggle + evidence links
 *
 * PRD §12.3.5 task 5 + §12.3.8 task 5.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { AlertCircle, Plus, Trash2, Users, MessageSquare, Sparkles } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Modal } from '@/components/ui/Modal/Modal';
import { FormSheet } from '@/components/ui/FormSheet';
import type { TabDef } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { DateInput } from '@/components/ui/DateInput';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { ImpactClassificationPicker, type ImpactClassification } from '@/components/roros/decisions/ImpactClassificationPicker';
import { DecisionMethodLibraryPanel } from '@/components/roros/decisions/DecisionMethodLibraryPanel';
import { AdvisoryBoardEmpty } from '@/components/roros/decisions/AdvisoryBoardEmpty';
import { useRorosDecisionCreate, useRorosDecisionUpdate, useRorosDecisionAdvisoryConsult, useRorosDecisionRunModerator, useRorosDecisionPreviewModerator, useRorosDecisionMethods, useRorosDecisionSubmit, useRorosDecisionApprove, useRorosDecisionReject } from '@/hooks/roros/decisions';
import { useAdvisoryPersonasForDomain } from '@/hooks/roros/advisory-personas';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useAlignmentCheck, useAlignmentAcknowledge, useAlignmentAcknowledgements } from '@/hooks/roros/alignment';
import { AlignmentWarnings } from '@/components/roros/alignment/AlignmentWarnings';
import { CompanyContextPanel } from '@/components/roros/alignment/CompanyContextPanel';
import type { AlignmentConflict } from '@/services/roros/alignment.service';
import type { DecisionMethodProfile, RorosDecisionNoteDTO } from '@/types/roros/decisions';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Evidence link ────────────────────────────────────────────────────────────

interface EvidenceLink {
  label: string;
  url: string;
}

// ─── Snapshot type ────────────────────────────────────────────────────────────

type DecisionSnapshot = {
  title: string;
  domainId: string;
  impactClassification: ImpactClassification | '';
  methodSlug: string;
  description: string;
  rationale: string;
  isTrial: boolean;
  trialEndDate: string;
  evidenceLinks: EvidenceLink[];
};

// ─── Per-field diff ───────────────────────────────────────────────────────────

function diffSnapshots(
  base: DecisionSnapshot,
  cur: DecisionSnapshot,
): Partial<Record<keyof DecisionSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    domainId: base.domainId !== cur.domainId,
    impactClassification: base.impactClassification !== cur.impactClassification,
    methodSlug: base.methodSlug !== cur.methodSlug,
    description: base.description !== cur.description,
    rationale: base.rationale !== cur.rationale,
    isTrial: base.isTrial !== cur.isTrial,
    trialEndDate: base.trialEndDate !== cur.trialEndDate,
    evidenceLinks: JSON.stringify(base.evidenceLinks) !== JSON.stringify(cur.evidenceLinks),
  };
}

// ─── Tab field assignments for per-tab dirty dots ─────────────────────────────

const DETAILS_FIELDS: Array<keyof DecisionSnapshot> = [
  'title', 'domainId', 'impactClassification', 'methodSlug', 'description', 'rationale',
];
const EVIDENCE_FIELDS: Array<keyof DecisionSnapshot> = [
  'isTrial', 'trialEndDate', 'evidenceLinks',
];

// ─── Field errors ─────────────────────────────────────────────────────────────

interface FieldErrors {
  title?: string;
  domainId?: string;
  impactClassification?: string;
  methodSlug?: string;
  description?: string;
  rationale?: string;
  general?: string;
}

// ─── Advisory consult dialog ──────────────────────────────────────────────────

interface ConsultDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (question: string) => void;
  isPending: boolean;
}

function AdvisoryConsultDialog({ isOpen, onClose, onSubmit, isPending }: ConsultDialogProps) {
  const [question, setQuestion] = useState('');
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('Consult Advisory Board')} size="md">
      <div className="space-y-4 pt-2">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Question for the advisory board')}
          </label>
          <Textarea
            rows={4}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('What question or scenario should the advisory board evaluate?')}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            isLoading={isPending}
            disabled={!question.trim() || isPending}
            onClick={() => onSubmit(question.trim())}
          >
            {t('Send Consultation')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Reject reason dialog ─────────────────────────────────────────────────────

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isPending: boolean;
}

function RejectReasonDialog({ isOpen, onClose, onSubmit, isPending }: RejectDialogProps) {
  const [reason, setReason] = useState('');
  // Reset reason field when the dialog opens for a new rejection
  useEffect(() => { if (isOpen) setReason(''); }, [isOpen]);
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('Reject Decision')} size="md">
      <div className="space-y-4 pt-2">
        <p className="text-sm text-muted-foreground">
          {t('Provide a brief reason for rejecting this decision. The reason is logged on the decision note for governance traceability.')}
        </p>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Reason for rejection')}
          </label>
          <Textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('e.g. Insufficient evidence; defer until customer interviews complete.')}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            isLoading={isPending}
            disabled={!reason.trim() || isPending}
            onClick={() => onSubmit(reason.trim())}
          >
            {t('Confirm Rejection')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DecisionFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (createdDecisionId?: string) => void;
  /** Pass an existing decision to open in EDIT mode with advisory/trial/evidence sections. */
  decision?: RorosDecisionNoteDTO | null;
  /**
   * J6 — pre-select an Impact Classification when opening in CREATE mode.
   *
   * Used by OperatingModePanel: an operating-mode change is, per GOV-03, always
   * a Trunk-impact decision, so the panel passes `defaultImpact='trunk'` to
   * make the docstring true and save the user a click. Ignored in EDIT mode
   * (the existing decision's impact wins).
   */
  defaultImpact?: ImpactClassification;
  /**
   * Wave 4c URL routing (opt-in). When set, FormSheet syncs ?urlKey=urlId in
   * the URL so the sheet is deep-linkable. Omit to opt out.
   */
  urlKey?: string;
  urlId?: string;
}

export function DecisionFormDialog({ isOpen, onClose, onCreated, decision, defaultImpact, urlKey, urlId }: DecisionFormDialogProps) {
  const isEditMode = !!decision;
  const createMutation = useRorosDecisionCreate();
  const updateMutation = useRorosDecisionUpdate();
  const advisoryConsultMutation = useRorosDecisionAdvisoryConsult();
  const runModeratorMutation = useRorosDecisionRunModerator();
  const previewModeratorMutation = useRorosDecisionPreviewModerator();
  const alignmentCheckMutation = useAlignmentCheck();
  const alignmentAcknowledgeMutation = useAlignmentAcknowledge();
  // AC-CCA-28: fetch prior acks from audit log (edit mode only; skipped when no decision.id)
  const { seededIds: seededAcknowledgedIds } = useAlignmentAcknowledgements(
    decision?.id ?? null,
    'decision',
  );
  // Status-transition mutations (Sven dogfood 2026-05-06): the hooks + API
  // endpoints existed since J5, but no UI button surfaced them, so a pending
  // decision could not be advanced past pending_approval through the UI.
  const submitMutation = useRorosDecisionSubmit();
  const approveMutation = useRorosDecisionApprove();
  const rejectMutation = useRorosDecisionReject();
  const domainsQuery = useRorosDomains();
  const activeDomains = useMemo(
    () => (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive),
    [domainsQuery.data],
  );

  // Method library — needed to look up the full DecisionMethodProfile by slug
  // when hydrating an existing decision in edit mode (Sven dogfood 2026-05-03:
  // pre-fix, the dialog showed "Choose Method..." even when a method was set
  // because selectedMethod was null — only methodSlug was hydrated).
  const decisionMethodsQuery = useRorosDecisionMethods();

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  const { state, setField, baseline, current, setBaseline } = useFormSnapshot<DecisionSnapshot>({
    initialFactory: () => {
      if (decision) {
        // Filter out the legacy auto-filled placeholder values so they don't
        // pre-pollute the textareas. Pre-fix, every decision was saved with
        // description=`<impact> impact decision` and rationale=`Method: <slug>`.
        // For decisions created post-fix, description/rationale are the real
        // values typed by the user. Heuristic: detect the placeholder pattern
        // and clear the field if it matches.
        const isPlaceholderDescription =
          /^(root|trunk|branch|leaf) impact decision\.?$/i.test(decision.description ?? '');
        const isPlaceholderRationale =
          /^Method: [a-z_]+$/i.test(decision.rationale ?? '');

        const refs = decision.evidenceRefs ?? [];
        const evidenceLinks: EvidenceLink[] = refs
          .filter((r: any) => r?.url)
          .map((r: any) => ({ label: r.label ?? '', url: r.url ?? '' }));

        return {
          title: decision.title ?? '',
          domainId: decision.domainId ?? '',
          impactClassification: (decision.impactClassification ?? '') as ImpactClassification | '',
          methodSlug: decision.methodUsed ?? '',
          description: isPlaceholderDescription ? '' : (decision.description ?? ''),
          rationale: isPlaceholderRationale ? '' : (decision.rationale ?? ''),
          isTrial: decision.isTrial ?? false,
          trialEndDate: decision.trialEvaluationDate ?? '',
          evidenceLinks,
        };
      }
      return {
        title: '',
        domainId: '',
        impactClassification: defaultImpact ?? '',
        methodSlug: '',
        description: '',
        rationale: '',
        isTrial: false,
        trialEndDate: '',
        evidenceLinks: [],
      };
    },
    isOpen,
    depKey: decision?.id ?? null,
  });

  // Destructure for convenience
  const {
    title, domainId, impactClassification, methodSlug,
    description, rationale, isTrial, trialEndDate, evidenceLinks,
  } = state;

  const [errors, setErrors] = useState<FieldErrors>({});
  // selectedMethod is ephemeral display state — NOT in the snapshot.
  const [selectedMethod, setSelectedMethod] = useState<DecisionMethodProfile | null>(null);
  const [isConsultDialogOpen, setIsConsultDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  // CCA-M1: alignment conflicts found after save
  const [alignmentConflicts, setAlignmentConflicts] = useState<AlignmentConflict[]>([]);
  const [acknowledgedPrincipleIds, setAcknowledgedPrincipleIds] = useState<Set<string>>(new Set());
  // decisionId after save — needed for acknowledge event attribution
  const [savedDecisionId, setSavedDecisionId] = useState<string | null>(null);

  // Advisory persona check for the decision's domain
  const advisoryPersonas = useAdvisoryPersonasForDomain(domainId || null);

  // Reset ephemeral state and mutations on close.
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setIsConsultDialogOpen(false);
      setSelectedMethod(null);
      setAlignmentConflicts([]);
      setAcknowledgedPrincipleIds(new Set());
      setSavedDecisionId(null);
    }
  }, [isOpen]);

  // AC-CCA-28: Seed acknowledgedPrincipleIds with prior acks when dialog opens (edit mode).
  // Merges seeded ids on top of any new session acks so re-opening shows prior acks.
  // Bail (return prev unchanged) when no new ids would be added — prevents React
  // from re-rendering and retriggering this effect (infinite loop guard).
  useEffect(() => {
    if (isOpen && seededAcknowledgedIds.size > 0) {
      setAcknowledgedPrincipleIds((prev) => {
        const merged = new Set(prev);
        let changed = false;
        seededAcknowledgedIds.forEach((id) => {
          if (!merged.has(id)) { merged.add(id); changed = true; }
        });
        return changed ? merged : prev;
      });
    }
  }, [isOpen, seededAcknowledgedIds]);

  // Reset moderator mutations on open (Sven dogfood 2026-05-03 seventh iteration:
  // also reset on OPEN, not just close — the parent renders two dialog instances
  // (create + edit), and switching between them can leave stale moderator output
  // from the prior session of the same dialog).
  useEffect(() => {
    if (!isOpen) return;
    previewModeratorMutation.reset();
    runModeratorMutation.reset();
    advisoryConsultMutation.reset();
    // Don't list the mutation objects in deps — they are stable references from
    // useMutation and listing them would cause an infinite reset loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Derive selectedMethod from methodSlug whenever the methods library loads
  // or methodSlug changes. This ensures the "chip" on the Details tab shows
  // the method name in both edit-mode hydration and after moderator auto-fill.
  useEffect(() => {
    if (!isOpen || !decisionMethodsQuery.data) return;
    if (methodSlug) {
      const found = decisionMethodsQuery.data.find((m: DecisionMethodProfile) => m.slug === methodSlug);
      setSelectedMethod(found ?? null);
    } else {
      setSelectedMethod(null);
    }
  }, [isOpen, methodSlug, decisionMethodsQuery.data]);

  /**
   * F-Ref-25 / W4c — All 6 fields are intentionally REQUIRED.
   *
   * Decision Notes without substance are not useful for governance traceability.
   * A record that lacks its title, decision text, rationale, domain, impact
   * classification, or method cannot be moderated, referenced in Exploration
   * Zones, or surfaced correctly in the Decision Log filters.
   *
   * PM decision (2026-05-24): KEEP all 6 required. Do not relax to optional.
   * Rationale: the Decision Moderator also depends on these fields being set
   * to produce a meaningful classification (see §12.3.5 task 5).
   */
  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!title.trim()) e.title = t('Title is required');
    if (!domainId) e.domainId = t('Domain is required');
    if (!impactClassification) e.impactClassification = t('Impact classification is required');
    if (!methodSlug) e.methodSlug = t('Decision method is required');
    if (!description.trim()) e.description = t('Decision text is required');
    if (!rationale.trim()) e.rationale = t('Rationale is required');
    return e;
  };

  // Evidence link helpers
  const addEvidenceLink = () => setField('evidenceLinks', [...evidenceLinks, { label: '', url: '' }]);
  const removeEvidenceLink = (idx: number) =>
    setField('evidenceLinks', evidenceLinks.filter((_, i) => i !== idx));
  const updateEvidenceLink = (idx: number, field: keyof EvidenceLink, value: string) =>
    setField('evidenceLinks', evidenceLinks.map((link, i) => (i === idx ? { ...link, [field]: value } : link)));

  // Advisory consult submit
  //
  // Sven dogfood 2026-05-03 (fifth iteration): match the Decision Moderator's
  // preview UX — let the founder consult the advisory board straight from the
  // create dialog without first having to save and re-open. The advisory
  // synthesis is stored on the row, so the row has to exist — but instead of
  // forcing a manual save first, we auto-save here using the current form
  // values, then immediately run the consultation against the new id. To the
  // user it's one click.
  const handleAdvisoryConsult = async (question: string) => {
    // Edit mode: existing flow — just consult the saved decision.
    if (decision?.id) {
      advisoryConsultMutation.mutate(
        { id: decision.id, personaIds: advisoryPersonas.personas.map((p) => p.id), question },
        {
          onSuccess: () => {
            setIsConsultDialogOpen(false);
          },
          onError: (err: any) => {
            setErrors({ general: err?.message ?? t('Advisory consultation failed.') });
            setIsConsultDialogOpen(false);
          },
        },
      );
      return;
    }

    // Create mode: validate the form, save the decision, then consult.
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setIsConsultDialogOpen(false);
      return;
    }

    setErrors({});

    try {
      // Save the decision using the same payload shape as the Save button.
      const created = await createMutation.mutateAsync({
        title: title.trim(),
        domainId: domainId,  // A1: API uses camelCase domainId
        impact_classification: impactClassification,
        method_used: methodSlug,
        description: description.trim(),
        rationale: rationale.trim(),
      });

      if (!created?.id) {
        setErrors({ general: t('Failed to save decision before consulting.') });
        setIsConsultDialogOpen(false);
        return;
      }

      // Consult against the just-saved id.
      await advisoryConsultMutation.mutateAsync({
        id: created.id,
        personaIds: advisoryPersonas.personas.map((p) => p.id),
        question,
      });

      setIsConsultDialogOpen(false);
      // Bypass dirty guard — save already consumed the form state.
      setBaseline(current);
      onClose();
      // Re-open in edit mode so the founder sees the synthesis inline.
      onCreated?.(created.id);
    } catch (err: any) {
      setErrors({ general: err?.message ?? t('Advisory consultation failed.') });
      setIsConsultDialogOpen(false);
    }
  };

  // ─── Tab navigation helper ──────────────────────────────────────────────────
  // FormSheet manages activeTab internally; the only way to programmatically
  // switch tabs from outside is to click the tab trigger via DOM. This is safe
  // because FormSheet renders the triggers with stable data-testid values.
  const switchToTab = useCallback((tabTestId: string) => {
    const trigger = document.querySelector<HTMLElement>(`[data-testid="${tabTestId}"]`);
    trigger?.click();
  }, []);

  const handleMethodSelect = useCallback((method: DecisionMethodProfile) => {
    setSelectedMethod(method);
    setField('methodSlug', method.slug);
    setErrors((prev) => ({ ...prev, methodSlug: undefined }));
    // After picking a method, switch back to the Details tab so the user
    // sees the selected method chip. Uses DOM-based tab navigation because
    // FormSheet owns the activeTab state internally.
    switchToTab('decision-tab-details');
  }, [setField, switchToTab]);

  // ─── Status transitions (Sven dogfood 2026-05-06) ──────────────────────────

  const handleSubmitForApproval = () => {
    if (!decision?.id) return;
    setErrors({});
    submitMutation.mutate(decision.id, {
      onSuccess: () => {
        // Don't close — let the user see the dialog refresh with Approve/Reject
        // visible. The decision query is invalidated by the hook.
        onCreated?.();
      },
      onError: (err: any) => {
        setErrors({ general: err?.message ?? t('Failed to submit decision for approval') });
      },
    });
  };

  const handleApprove = () => {
    if (!decision?.id) return;
    setErrors({});
    approveMutation.mutate(
      { id: decision.id },
      {
        onSuccess: () => {
          setBaseline(current);
          onClose();
          onCreated?.();
        },
        onError: (err: any) => {
          setErrors({ general: err?.message ?? t('Failed to approve decision') });
        },
      },
    );
  };

  const handleReject = (reason: string) => {
    if (!decision?.id) return;
    setErrors({});
    rejectMutation.mutate(
      { id: decision.id, reason: reason || undefined },
      {
        onSuccess: () => {
          setIsRejectDialogOpen(false);
          setBaseline(current);
          onClose();
          onCreated?.();
        },
        onError: (err: any) => {
          setErrors({ general: err?.message ?? t('Failed to reject decision') });
          setIsRejectDialogOpen(false);
        },
      },
    );
  };

  const handleSubmit = () => {
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    const evidenceRefs = evidenceLinks
      .filter((l) => l.url.trim())
      .map((l) => ({ label: l.label.trim() || l.url.trim(), url: l.url.trim() }));

    if (isEditMode && decision) {
      // Edit path — update the existing decision
      updateMutation.mutate(
        {
          id: decision.id,
          payload: {
            title: title.trim(),
            description: description.trim(),
            rationale: rationale.trim(),
            is_trial: isTrial,
            trial_evaluation_date: isTrial && trialEndDate ? trialEndDate : undefined,
            evidence_refs: evidenceRefs,
          },
        },
        {
          onSuccess: () => {
            setBaseline(current);
            onClose();
            onCreated?.();
          },
          onError: (err: any) => {
            setErrors({ general: err?.message ?? t('Failed to update decision note') });
          },
        },
      );
      return;
    }

    // Create path
    createMutation.mutate(
      {
        title: title.trim(),
        domainId: domainId,
        impact_classification: impactClassification,
        method_used: methodSlug,
        // description = the actual decision; rationale = why. Both required
        // by Zod (min(1)). Pre-fix this dialog auto-filled placeholders
        // because the user had no field to type the real values.
        description: description.trim(),
        rationale: rationale.trim(),
      },
      {
        onSuccess: (created) => {
          setBaseline(current);
          // CCA-M1: run alignment check after save (non-blocking — decision already saved)
          const createdId = created?.id ?? null;
          setSavedDecisionId(createdId);
          alignmentCheckMutation.mutate(
            {
              subject: {
                title: title.trim(),
                description: description.trim() || null,
                rationale: rationale.trim() || null,
              },
            },
            {
              onSuccess: (result) => {
                if (result.conflicts.length > 0) {
                  // Show conflicts — do NOT close dialog yet
                  setAlignmentConflicts(result.conflicts);
                  return;
                }
                // No conflicts — proceed to close + callback
                onClose();
                onCreated?.(created?.id);
              },
              onError: () => {
                // Alignment check failure is non-blocking — proceed normally
                onClose();
                onCreated?.(created?.id);
              },
            },
          );
        },
        onError: (err: any) => {
          // Surface ROROS_DECISION_NOTE_IMPACT_REQUIRED or other field-level errors
          const code = err?.code;
          const message = err?.message ?? t('Failed to create decision note');

          if (
            code === 'ROROS_DECISION_NOTE_IMPACT_REQUIRED' ||
            message?.toLowerCase().includes('impact_classification')
          ) {
            setErrors({
              impactClassification: t('Impact classification is required'),
              general: message,
            });
          } else if (message?.toLowerCase().includes('method')) {
            setErrors({ methodSlug: message });
          } else {
            setErrors({ general: message });
          }
        },
      },
    );
  };

  const isMutationPending = createMutation.isPending || updateMutation.isPending;

  // ─── Status-derived UI flags (Sven dogfood 2026-05-06) ──────────────────────
  // Drives which footer buttons render. Mirrors the backend's allowed
  // transitions: draft/deferred → pending_approval → approved/rejected.
  // Terminal states (approved/rejected/archived) lock the dialog read-only.
  const decisionStatus = decision?.status ?? null;
  const canSaveBody = !isEditMode || decisionStatus === 'draft' || decisionStatus === 'deferred';
  const canSubmitForApproval = isEditMode && (decisionStatus === 'draft' || decisionStatus === 'deferred');
  const canApproveOrReject = isEditMode && decisionStatus === 'pending_approval';
  const isAnyTransitionPending =
    submitMutation.isPending || approveMutation.isPending || rejectMutation.isPending;

  // ─── headerExtras — status-transition buttons ────────────────────────────────
  // These appear in the FormSheet sticky header alongside Cancel / Save.
  const headerExtras = (
    <>
      {canSubmitForApproval && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          isLoading={submitMutation.isPending}
          disabled={isMutationPending || isAnyTransitionPending}
          onClick={handleSubmitForApproval}
          data-testid="decision-submit-for-approval"
        >
          {t('Submit for Approval')}
        </Button>
      )}
      {canApproveOrReject && (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            isLoading={rejectMutation.isPending}
            disabled={isAnyTransitionPending}
            onClick={() => setIsRejectDialogOpen(true)}
            data-testid="decision-reject"
          >
            {t('Reject')}
          </Button>
          <Button
            type="button"
            size="sm"
            isLoading={approveMutation.isPending}
            disabled={isAnyTransitionPending}
            onClick={handleApprove}
            data-testid="decision-approve"
          >
            {t('Approve')}
          </Button>
        </>
      )}
    </>
  );

  // ─── Tab 1: Details ──────────────────────────────────────────────────────────

  const detailsTabContent = (
    <div className="space-y-5 pt-2">
      {/* General error */}
      {errors.general && (
        <Alert variant="error" title={t('Error')}>
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
            <span>{errors.general}</span>
          </div>
        </Alert>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {t('Title')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        <Input
          type="text"
          value={title}
          onChange={(e) => {
            setField('title', e.target.value);
            if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
          }}
          error={errors.title}
          placeholder={t('e.g. "Switch to Postgres" or "Hire first VP Sales"')}
          maxLength={500}
          required
          autoFocus
        />
      </div>

      {/* Decision (description) — what was actually decided */}
      <div>
        <label htmlFor="decision-text" className="block text-sm font-medium text-foreground mb-1">
          {t('Decision')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        <Textarea
          id="decision-text"
          value={description}
          onChange={(e) => {
            setField('description', e.target.value);
            if (errors.description) setErrors((prev) => ({ ...prev, description: undefined }));
          }}
          error={errors.description}
          placeholder={t('What was decided? e.g. "We will run a 6-week pilot with 3 enterprise customers in DACH, contracts signed by end of Q3."')}
          rows={4}
          required
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {t('The actual decision content. Keep it specific and actionable.')}
        </p>
      </div>

      {/* Rationale — why */}
      <div>
        <label htmlFor="decision-rationale" className="block text-sm font-medium text-foreground mb-1">
          {t('Rationale')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        <Textarea
          id="decision-rationale"
          value={rationale}
          onChange={(e) => {
            setField('rationale', e.target.value);
            if (errors.rationale) setErrors((prev) => ({ ...prev, rationale: undefined }));
          }}
          error={errors.rationale}
          placeholder={t('Why this decision? What was the reasoning, what alternatives were considered, what trade-offs accepted?')}
          rows={3}
          required
        />
      </div>

      {/* Domain */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {t('Domain')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        <Select
          value={domainId}
          onChange={(e) => {
            setField('domainId', e.target.value);
            if (errors.domainId) setErrors((prev) => ({ ...prev, domainId: undefined }));
          }}
          error={errors.domainId}
        >
          <option value="">{t('Select a domain…')}</option>
          {activeDomains.map((d: RorosDomainDTO) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
      </div>

      {/* ── A.1 Decision Moderator (visible in BOTH create + edit modes) ───
          Sven dogfood 2026-05-03 (third iteration):
            • create mode → uses previewModerator (no DB write).
              User clicks "Run Decision Moderator" while drafting,
              LLM returns impact + method + rationale, the form
              auto-fills those fields. User reviews + Save when ready.
            • edit mode → uses runFullModerator (persisted, advisory
              flow runs for trunk+ decisions).
          Block only on rejected/archived in edit mode (terminal). */}
      {(!isEditMode || (decision?.status !== 'rejected' && decision?.status !== 'archived')) && (
        <div className="pt-2 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-primary-600" aria-hidden />
            {t('Decision Moderator')}
          </h3>
          {/* In edit mode show the persisted rationale; in create or
              after a fresh preview show the preview's rationale. */}
          {isEditMode && decision?.moderatorRationale ? (
            <Alert variant="success" className="text-xs mb-2">
              <strong>{t('Recommended:')}</strong> {decision.moderatorRationale}
            </Alert>
          ) : previewModeratorMutation.data && !isEditMode ? (
            <Alert variant="success" className="text-xs mb-2">
              <strong>{t('Recommended:')}</strong> {previewModeratorMutation.data.rationale}
              <span className="block mt-1 text-muted-foreground">
                {t('Impact + method pre-filled below — review and Save.')}
              </span>
            </Alert>
          ) : (
            <p className="text-xs text-muted-foreground mb-2">
              {isEditMode
                ? t('Let the Decision Moderator classify this decision and recommend a method.')
                : t('Let the Decision Moderator suggest impact + method based on what you typed above. Run it before saving.')}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (isEditMode && decision?.id) {
                runModeratorMutation.mutate({ id: decision.id });
                return;
              }
              // Create mode — preview against current draft fields,
              // then auto-fill impact + method on success.
              if (!title.trim()) {
                setErrors((prev) => ({ ...prev, title: t('Type a title before running the moderator.') }));
                return;
              }
              previewModeratorMutation.mutate(
                {
                  title: title.trim(),
                  description: description.trim() || null,
                  domainId: domainId || null,
                },
                {
                  onSuccess: (preview) => {
                    // Auto-fill every field the moderator returned.
                    // User can still override before Save.
                    //
                    // Sven dogfood 2026-05-03 (fourth iteration):
                    //   pre-fix the preview pre-filled impact + method
                    //   only, leaving the founder to retype the actual
                    //   decision text and rationale. The moderator's
                    //   `decisionStatement` is a polished version of
                    //   what the founder typed; `rationale` is the
                    //   moderator's reasoning. Pre-fill both so the
                    //   form is ready to save in one click.
                    setField('impactClassification', preview.impact as ImpactClassification);
                    setField('methodSlug', preview.methodSlug);
                    // Only overwrite the user's typed decision text
                    // when the moderator returned a polished version.
                    if (preview.decisionStatement?.trim()) {
                      setField('description', preview.decisionStatement);
                    }
                    // Rationale is always the moderator's reasoning.
                    setField('rationale', preview.rationale);
                    setErrors({});
                  },
                },
              );
            }}
            disabled={runModeratorMutation.isPending || previewModeratorMutation.isPending}
            data-testid="run-decision-moderator-btn"
          >
            <Sparkles className="h-4 w-4 mr-1.5" aria-hidden />
            {(runModeratorMutation.isPending || previewModeratorMutation.isPending)
              ? t('Running…')
              : t('Run Decision Moderator')}
          </Button>
          {/* Persistent error panel for both run + preview paths. */}
          {((runModeratorMutation.isError && !runModeratorMutation.isPending) ||
            (previewModeratorMutation.isError && !previewModeratorMutation.isPending)) && (
            <div
              className="mt-2 rounded-md bg-error-50 border border-error-200 p-2 text-xs text-error-700"
              data-testid="run-decision-moderator-error"
              role="alert"
            >
              <p className="font-semibold mb-0.5">{t('Decision Moderator failed')}</p>
              <p className="whitespace-pre-wrap">
                {((runModeratorMutation.error as any)?.message
                  ?? (previewModeratorMutation.error as any)?.message)
                  ?? t('Moderator failed — try again.')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Impact Classification */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {t('Impact Classification')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        {/* Inline error for impact (GOV-01 / ROROS_DECISION_NOTE_IMPACT_REQUIRED) */}
        {errors.impactClassification && (
          <Alert variant="error" title={t('Impact required')} className="mb-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
              <span>{errors.impactClassification}</span>
            </div>
          </Alert>
        )}
        <ImpactClassificationPicker
          value={impactClassification || undefined}
          onChange={(val) => {
            setField('impactClassification', val);
            if (errors.impactClassification)
              setErrors((prev) => ({ ...prev, impactClassification: undefined }));
          }}
          error={errors.impactClassification}
        />
      </div>

      {/* CCA-M1: Alignment Warnings (shown after a check is run or after save) */}
      {alignmentConflicts.length > 0 && (
        <div className="pt-2 border-t border-warning-200">
          <AlignmentWarnings
            conflicts={alignmentConflicts}
            acknowledgedIds={acknowledgedPrincipleIds}
            isAcknowledging={alignmentAcknowledgeMutation.isPending}
            acknowledgeEnabled={!!(savedDecisionId ?? decision?.id)}
            onAcknowledge={(principleId) => {
              // [2] Guard: decisionId must exist (post-save) before acknowledging.
              // Pre-save, savedDecisionId is null and decision is undefined, so
              // we skip the mutation to avoid a 422 from the UUID schema.
              const decisionId = savedDecisionId ?? decision?.id;
              if (!decisionId) return;
              alignmentAcknowledgeMutation.mutate(
                { decisionId, principleId },
                {
                  onSuccess: () => {
                    setAcknowledgedPrincipleIds((prev) => new Set([...prev, principleId]));
                  },
                },
              );
            }}
          />
          {/* Continue to close after acknowledging — or the user can dismiss manually */}
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                onClose();
                onCreated?.(savedDecisionId ?? undefined);
              }}
              data-testid="alignment-dismiss-continue"
            >
              {t('Continue anyway')}
            </Button>
          </div>
        </div>
      )}

      {/* CCA-M1: Check alignment button (in create mode only, before save) */}
      {!isEditMode && alignmentConflicts.length === 0 && (
        <div className="pt-2 border-t border-border">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (!title.trim()) {
                setErrors((prev) => ({
                  ...prev,
                  title: t('Type a title before checking alignment.'),
                }));
                return;
              }
              alignmentCheckMutation.mutate(
                {
                  subject: {
                    title: title.trim(),
                    description: description.trim() || null,
                    rationale: rationale.trim() || null,
                  },
                },
                {
                  onSuccess: (result) => {
                    setAlignmentConflicts(result.conflicts);
                  },
                },
              );
            }}
            disabled={alignmentCheckMutation.isPending}
            isLoading={alignmentCheckMutation.isPending}
            data-testid="check-alignment-btn"
          >
            {t('Check alignment')}
          </Button>
        </div>
      )}

      {/* Decision Method */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {t('Decision Method')} <span className="text-error-500" aria-hidden>*</span>
        </label>
        {selectedMethod ? (
          <div className="flex items-center justify-between rounded-md border border-primary-300 bg-primary-50 px-3 py-2">
            <span className="text-sm font-medium text-foreground">{selectedMethod.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => switchToTab('decision-tab-methods')}
            >
              {t('Change')}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => switchToTab('decision-tab-methods')}
            className={errors.methodSlug ? 'border-error-500' : ''}
          >
            {t('Choose Method…')}
          </Button>
        )}
        {errors.methodSlug && (
          <p role="alert" className="mt-1 text-xs text-error-600">{errors.methodSlug}</p>
        )}
      </div>

    </div>
  );

  // ─── Tab 5: Company Context ───────────────────────────────────────────────────
  // AC-CCA-23 (placement rework): dedicated tab keeps the panel above-the-fold
  // and visible without scrolling, vs the buried inline position at the bottom
  // of the Details tab.

  const companyContextTabContent = (
    <div className="pt-2">
      <CompanyContextPanel data-testid="decision-company-context-panel" />
    </div>
  );

  // ─── Tab 2: Method Library ────────────────────────────────────────────────────

  const methodsTabContent = (
    <div className="pt-2 space-y-3">
      <p className="text-sm text-muted-foreground">
        {t('Choose a decision method. Click a card to select it and return to the Details tab.')}
      </p>
      <DecisionMethodLibraryPanel
        onSelect={handleMethodSelect}
        selectedSlug={methodSlug || undefined}
      />
    </div>
  );

  // ─── Tab 3: Advisory ──────────────────────────────────────────────────────────

  const advisoryTabContent = (
    <div className="space-y-5 pt-2">
      {/* General error shown here too if advisory action fails */}
      {errors.general && (
        <Alert variant="error" title={t('Error')}>
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
            <span>{errors.general}</span>
          </div>
        </Alert>
      )}

      {/* Advisory Board Consultation */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
          <Users className="h-4 w-4" aria-hidden />
          {t('Advisory Board Consultation')}
        </h3>

        {advisoryPersonas.isLoading ? (
          <p className="text-xs text-muted-foreground">{t('Checking advisory board…')}</p>
        ) : advisoryPersonas.hasAny ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {advisoryPersonas.personas.length + ' ' + t('Knowledge Persona(s) available for this domain.')}
            </p>
            {decision?.advisoryConsulted && (
              <>
                <div className="flex items-center gap-1.5 text-xs text-success-600">
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                  {t('Advisory board has been consulted.')}
                </div>
                {/* F6 dogfood-fix-wave-1: 3-tier advisory display
                    1. Summary (always visible, prominent)
                    2. Synthesis (collapsible details, default-open)
                    3. Perspectives (collapsible per-persona cards) */}
                {(() => {
                  const ac = decision?.advisoryConsultation;
                  if (!ac) return null;
                  const perspectiveCount = Array.isArray(ac.perspectives) ? ac.perspectives.length : 0;
                  // Graceful fallback: if no summary but synthesis exists, use first 200 chars
                  const summaryText = ac.summary ?? (ac.synthesis ? ac.synthesis.slice(0, 200) : null);
                  return (
                    <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 space-y-3">
                      {/* 1. Summary — always visible */}
                      {summaryText && (
                        <div data-testid="advisory-summary">
                          <p className="text-sm font-medium text-foreground whitespace-pre-wrap">{summaryText}</p>
                        </div>
                      )}
                      {/* 2. Synthesis — collapsible, default-closed */}
                      {ac.synthesis && (
                        <details data-testid="advisory-synthesis" className="text-xs">
                          <summary className="cursor-pointer font-semibold text-foreground mb-1 select-none">
                            {t('Full Synthesis')}
                          </summary>
                          <p className="mt-1 text-foreground whitespace-pre-wrap">{ac.synthesis}</p>
                        </details>
                      )}
                      {/* 3. Perspectives — collapsible, default-closed with count badge */}
                      {perspectiveCount > 0 && (
                        <details data-testid="advisory-perspectives" className="text-xs">
                          <summary className="cursor-pointer font-semibold text-foreground select-none flex items-center gap-1.5">
                            {t('Perspectives')}
                            <span className="inline-flex items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground border">
                              {perspectiveCount}
                            </span>
                          </summary>
                          <div className="mt-2 space-y-2">
                            {(ac.perspectives ?? []).map((p, i) => (
                              <div key={i} className="text-foreground">
                                <span className="font-medium">{p.personaName ?? t('Persona')}:</span>{' '}
                                <span className="text-muted-foreground whitespace-pre-wrap">{p.perspective}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
            <div className="space-y-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsConsultDialogOpen(true)}
                // Sven dogfood 2026-05-03 (fifth iteration): button
                // is now functional in both modes. In create mode
                // the handler auto-saves the decision before
                // running the consultation — no save-and-reopen
                // dance needed.
                disabled={
                  advisoryConsultMutation.isPending
                  || createMutation.isPending
                }
                data-testid="consult-advisory-board-btn"
              >
                <MessageSquare className="h-4 w-4 mr-1.5" aria-hidden />
                {t('Consult Advisory Board')}
              </Button>
              {!isEditMode && (
                <p className="text-xs text-muted-foreground">
                  {t('We will save the decision and then consult the advisory board.')}
                </p>
              )}
            </div>
          </div>
        ) : (
          <AdvisoryBoardEmpty
            domainId={decision?.domainId ?? domainId}
            data-testid="advisory-board-empty"
          />
        )}
      </div>
    </div>
  );

  // ─── Tab 4: Evidence ──────────────────────────────────────────────────────────

  const evidenceTabContent = (
    <div className="space-y-5 pt-2">
      {/* Trial Period Toggle */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">{t('Trial Period')}</p>
            <p className="text-xs text-muted-foreground">
              {t('Mark this decision as a time-limited trial with an evaluation date.')}
            </p>
          </div>
          <Switch
            checked={isTrial}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('isTrial', e.target.checked)}
            aria-label={t('Trial period')}
            data-testid="trial-period-toggle"
          />
        </div>
        {isTrial && (
          <div className="mt-2">
            <label className="block text-xs font-medium text-foreground mb-1">
              {t('Trial end date')}
            </label>
            <DateInput
              value={trialEndDate}
              onChange={(isoDate) => setField('trialEndDate', isoDate)}
              data-testid="trial-end-date-input"
            />
          </div>
        )}
      </div>

      {/* Evidence Links */}
      <div className="pt-2 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-foreground">{t('Evidence Links')}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addEvidenceLink}
            data-testid="add-evidence-link-btn"
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden />
            {t('Add link')}
          </Button>
        </div>
        {evidenceLinks.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t('No evidence links yet. Add URLs that support this decision.')}
          </p>
        )}
        <div className="space-y-2">
          {evidenceLinks.map((link, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                type="text"
                value={link.label}
                onChange={(e) => updateEvidenceLink(idx, 'label', e.target.value)}
                className="flex-1"
                placeholder={t('Label (optional)')}
                data-testid={`evidence-link-label-${idx}`}
              />
              <Input
                type="url"
                value={link.url}
                onChange={(e) => updateEvidenceLink(idx, 'url', e.target.value)}
                className="flex-2 min-w-0 w-full"
                placeholder={t('https://…')}
                data-testid={`evidence-link-url-${idx}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeEvidenceLink(idx)}
                aria-label={t('Remove evidence link')}
                data-testid={`evidence-link-remove-${idx}`}
                className="flex-shrink-0 hover:text-error-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Tab definitions ─────────────────────────────────────────────────────────

  const tabs: Array<TabDef<DecisionSnapshot>> = [
    {
      key: 'form',
      label: t('Details'),
      fields: DETAILS_FIELDS,
      content: detailsTabContent,
      testId: 'decision-tab-details',
    },
    {
      key: 'methods',
      label: t('Method Library'),
      // No dirty fields — method selection updates methodSlug (Details tab field)
      content: methodsTabContent,
      testId: 'decision-tab-methods',
    },
    {
      key: 'advisory',
      label: t('Advisory'),
      // No dirty fields — consultation is an action, not a form field
      content: advisoryTabContent,
      testId: 'decision-tab-advisory',
    },
    {
      key: 'evidence',
      label: t('Evidence'),
      fields: EVIDENCE_FIELDS,
      content: evidenceTabContent,
      testId: 'decision-tab-evidence',
    },
    {
      // AC-CCA-23: dedicated "Company Context" tab — visible above-the-fold at authoring time
      key: 'context',
      label: t('Company Context'),
      content: companyContextTabContent,
      testId: 'decision-tab-context',
    },
  ];

  return (
    <>
      <FormSheet<DecisionSnapshot>
        isOpen={isOpen}
        onClose={onClose}
        title={isEditMode ? t('Edit Decision Note') : t('New Decision Note')}
        mode={isEditMode ? 'edit' : 'create'}
        baseline={baseline}
        current={current}
        diff={diffSnapshots}
        onSubmit={handleSubmit}
        isSubmitting={isMutationPending}
        submitDisabled={
          !canSaveBody ||
          isMutationPending ||
          isAnyTransitionPending ||
          (!isEditMode && (!title || !domainId || !impactClassification || !methodSlug || !description.trim() || !rationale.trim()))
        }
        submitLabel={isEditMode ? t('Save Changes') : t('Create Decision Note')}
        submittingLabel={t('Saving…')}
        width="md"
        tabs={tabs}
        tabsAriaLabel={t('Decision Note editor sections')}
        headerExtras={headerExtras}
        submitTestId="decision-form-save"
        urlKey={urlKey}
        urlId={urlId}
      />

      {/* Advisory consult dialog (portal outside main Sheet). The pending
          state combines the create + consult mutations because the create-mode
          flow runs them sequentially — the user sees one continuous wait
          cursor across save → consult. */}
      <AdvisoryConsultDialog
        isOpen={isConsultDialogOpen}
        onClose={() => setIsConsultDialogOpen(false)}
        onSubmit={handleAdvisoryConsult}
        isPending={advisoryConsultMutation.isPending || createMutation.isPending}
      />

      {/* Reject-reason dialog (Sven dogfood 2026-05-06) — fired from the
          header's Reject button when a decision is in pending_approval.
          Stays as a Modal layered on top of the Sheet per R2. */}
      <RejectReasonDialog
        isOpen={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onSubmit={handleReject}
        isPending={rejectMutation.isPending}
      />
    </>
  );
}
